/**
 * Die Partiekonfiguration — §42, §67 Schritte 5 bis 11.
 *
 * **Konfiguration und Lobby sind ein Bildschirm** (Nr. 27, `GAME_RULES.md` Abschnitt 12). Dieses
 * Formular ist seine erste Hälfte: es läuft, bis die Partie existiert. Danach übernimmt die
 * Lobby — und sie bleibt bis zum Start veränderbar, weil sie sich füllt, während der Host noch
 * konfiguriert.
 *
 * **Der Host ist Spieler Nummer eins.** Er gibt Anzeigenamen und Farbe mit an; eine Partie hat
 * damit von ihrem ersten Moment an einen zugeordneten Spieler. Dass seine Farbe ein
 * konfigurierter Platz sein muss, prüft auch das Schema — hier wird es nur schon vorher
 * sichergestellt, statt dem Host eine 400 zu zeigen.
 *
 * **Die Siegbedingung bestimmt die Bedenkzeit** (Nr. 20, Nr. 22), und zwar als **Form**: Variante
 * A hat kein Feld dafür, Variante B verlangt eines. Die unmögliche Kombination entsteht gar
 * nicht erst — dieselbe Entscheidung wie im Schema, wo sie eine diskriminierte Union ist.
 *
 * **Die Vorschläge sind Vorschläge** (`lobby/presets.ts`): der Host wählt Sektoren frei (Nr. 8)
 * und bildet Teams frei (Nr. 24, auch ungleich). Was hier vorbelegt wird, ist Geometrie aus der
 * Engine und keine Regel.
 *
 * **Kein Balancewert steht hier.** Punkteziel und Score-Reset kommen als `defaults` vom Dienst,
 * aus der globalen Konfiguration (`R10`), und werden **vorbelegt angezeigt und ausdrücklich
 * mitgeschickt**. Bis `R10` standen beide leer, und erst der Dienst füllte die Lücke. Der Host sah
 * nicht, was gilt, und ein leerer Haken konnte den Reset bei der Vorgabe „an“ nie ausschalten.
 * Eine Zahl im Code wäre weiter eine zweite Wahrheit (`CLAUDE.md` Regel 2). Ein **geleertes**
 * Punkteziel bleibt beim globalen Wert.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SECTOR_IDS, type SectorId } from '@flaghex/engine';
import type {
  CreateGameRequest,
  GameDefaults,
  GameSettingsDto,
  SlotConfigDto,
} from '@flaghex/contracts';

import type { ApiFailure } from '../api/transport.js';
import { MODE_PRESETS, presetSlots, type PresetId } from './presets.js';

export interface CreateGameFormProps {
  readonly onCreate: (request: CreateGameRequest) => void;
  /** Punkteziel und Score-Reset aus der globalen Konfiguration — Pflicht, siehe oben. */
  readonly defaults: GameDefaults;
  readonly failure?: ApiFailure | undefined;
}

/** Eine Zahl aus einem Eingabefeld, oder `undefined`, wenn dort nichts Brauchbares steht. */
function intOf(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return undefined;
  }
  const value = Number(trimmed);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

export function CreateGameForm({ onCreate, defaults, failure }: CreateGameFormProps) {
  const { t } = useTranslation(['lobby', 'sectors', 'errors', 'common']);

  const [mode, setMode] = useState<PresetId>('duel');
  const [playerCount, setPlayerCount] = useState(2);
  const [slots, setSlots] = useState<readonly SlotConfigDto[]>(() => presetSlots('duel', 2));
  const [hostName, setHostName] = useState('');
  const [hostSector, setHostSector] = useState<SectorId | undefined>(undefined);
  const [victory, setVictory] = useState<'POINTS' | 'TIME'>('POINTS');
  const [timeBudget, setTimeBudget] = useState('10');
  const [setupTime, setSetupTime] = useState('');
  const [pointTarget, setPointTarget] = useState(() => String(defaults.pointTarget));
  const [resetScore, setResetScore] = useState(defaults.resetScoreOnFlagCapture);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [complaint, setComplaint] = useState<string | undefined>(undefined);

  /** Die Farbe des Hosts: ausdrücklich gewählt, sonst der erste Platz. */
  const effectiveHostSector = useMemo<SectorId | undefined>(() => {
    const chosen = slots.find((slot) => slot.sector === hostSector)?.sector;
    return (chosen ?? slots[0]?.sector) as SectorId | undefined;
  }, [slots, hostSector]);

  const applyMode = (next: PresetId): void => {
    const counts = MODE_PRESETS.find((preset) => preset.id === next)?.playerCounts ?? [2];
    const count = counts.includes(playerCount) ? playerCount : (counts[0] as number);
    setMode(next);
    setPlayerCount(count);
    setSlots(presetSlots(next, count));
    setHostSector(undefined);
  };

  const applyPlayerCount = (count: number): void => {
    setPlayerCount(count);
    setSlots(presetSlots(mode, count));
    setHostSector(undefined);
  };

  const changeSlot = (index: number, change: Partial<SlotConfigDto>): void => {
    setSlots((current) =>
      current.map((slot, at) => {
        if (at !== index) return slot;
        const next = { ...slot, ...change };
        // `team: ''` heißt „kein Team" — ein leerer Bezeichner wäre im Schema ungültig.
        return next.team === undefined || next.team === ''
          ? { sector: next.sector }
          : { sector: next.sector, team: next.team };
      }),
    );
  };

  const settingsOf = (): GameSettingsDto | undefined => {
    const common = {
      ...(intOf(pointTarget) === undefined ? {} : { pointTarget: intOf(pointTarget) as number }),
      // Leer heißt **unbegrenzt** (§42) — ein Wert, kein fehlendes Feld.
      setupTimeMinutes: intOf(setupTime) ?? null,
      // **Immer** ausdrücklich — auch `false`. Ein fehlendes Feld hieße „globale Vorgabe“, und
      // die ist vielleicht „an“ (der Fehler aus `R10`).
      resetScoreOnFlagCapture: resetScore,
      // Nur was ausdrücklich eingeschaltet ist: der Default (aus) steht im Server, nicht hier.
      ...(showCoordinates ? { showCoordinates: true } : {}),
    };
    if (victory === 'POINTS') {
      return { victory: 'POINTS', ...common };
    }
    const budget = intOf(timeBudget);
    if (budget === undefined) {
      return undefined;
    }
    return { victory: 'TIME', timeBudgetMinutes: budget, ...common };
  };

  const submit = (): void => {
    setComplaint(undefined);
    if (hostName.trim() === '') {
      setComplaint(t('lobby:create.hostName'));
      return;
    }
    if (new Set(slots.map((slot) => slot.sector)).size !== slots.length) {
      setComplaint(t('lobby:create.slotSector'));
      return;
    }
    const settings = settingsOf();
    if (settings === undefined) {
      setComplaint(t('lobby:create.timeBudget'));
      return;
    }
    const sector = effectiveHostSector;
    if (sector === undefined) {
      setComplaint(t('lobby:create.hostSector'));
      return;
    }
    onCreate({ settings, slots: [...slots], host: { displayName: hostName.trim(), sector } });
  };

  const counts = MODE_PRESETS.find((preset) => preset.id === mode)?.playerCounts ?? [];

  return (
    <section>
      <h2>{t('lobby:create.heading')}</h2>
      <p>{t('lobby:create.intro')}</p>

      <p>
        <label>
          {t('lobby:create.mode')}{' '}
          <select
            data-field="mode"
            value={mode}
            onChange={(event) => applyMode(event.target.value as PresetId)}
          >
            {MODE_PRESETS.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {t(`lobby:modes.${preset.id}`)}
              </option>
            ))}
          </select>
        </label>
      </p>

      {counts.length > 1 ? (
        <p>
          <label>
            {t('lobby:create.playerCount')}{' '}
            <select
              data-field="playerCount"
              value={String(playerCount)}
              onChange={(event) => applyPlayerCount(Number(event.target.value))}
            >
              {counts.map((count) => (
                <option key={count} value={String(count)}>
                  {count}
                </option>
              ))}
            </select>
          </label>
        </p>
      ) : null}

      <fieldset>
        <legend>{t('lobby:create.slots')}</legend>
        <p>{t('lobby:create.sectorHint')}</p>
        <p>{t('lobby:create.teamHint')}</p>
        <ul>
          {slots.map((slot, index) => (
            <li key={index}>
              <label>
                {t('lobby:create.slotSector')}{' '}
                <select
                  data-field={`slot.${index}.sector`}
                  value={slot.sector}
                  onChange={(event) => changeSlot(index, { sector: event.target.value as SectorId })}
                >
                  {SECTOR_IDS.map((sector) => (
                    <option key={sector} value={sector}>
                      {t(`sectors:${sector}`)}
                    </option>
                  ))}
                </select>
              </label>{' '}
              <label>
                {t('lobby:create.slotTeam')}{' '}
                <input
                  type="text"
                  data-field={`slot.${index}.team`}
                  value={slot.team ?? ''}
                  placeholder={t('lobby:create.noTeam')}
                  onChange={(event) => changeSlot(index, { team: event.target.value })}
                />
              </label>
            </li>
          ))}
        </ul>
      </fieldset>

      <fieldset>
        <legend>{t('lobby:create.victoryHeading')}</legend>
        <p>
          <label>
            {t('lobby:create.victory')}{' '}
            <select
              data-field="victory"
              value={victory}
              onChange={(event) => setVictory(event.target.value as 'POINTS' | 'TIME')}
            >
              <option value="POINTS">{t('lobby:victory.POINTS')}</option>
              <option value="TIME">{t('lobby:victory.TIME')}</option>
            </select>
          </label>
        </p>
        <p>{victory === 'POINTS' ? t('lobby:victory.pointsHint') : t('lobby:victory.timeHint')}</p>

        {victory === 'TIME' ? (
          <p>
            <label>
              {t('lobby:create.timeBudget')}{' '}
              <input
                type="number"
                min={1}
                step={1}
                data-field="timeBudget"
                value={timeBudget}
                onChange={(event) => setTimeBudget(event.target.value)}
              />
            </label>
          </p>
        ) : (
          <p>
            <label>
              {t('lobby:create.pointTarget')}{' '}
              <input
                type="number"
                min={1}
                step={1}
                data-field="pointTarget"
                value={pointTarget}
                onChange={(event) => setPointTarget(event.target.value)}
              />
            </label>
          </p>
        )}

        <p>
          <label>
            {t('lobby:create.setupTime')}{' '}
            <input
              type="number"
              min={1}
              step={1}
              data-field="setupTime"
              value={setupTime}
              onChange={(event) => setSetupTime(event.target.value)}
            />
            {` (${t('lobby:create.setupTimeHint')})`}
          </label>
        </p>

        <p>
          <label>
            <input
              type="checkbox"
              data-field="resetScore"
              checked={resetScore}
              onChange={(event) => setResetScore(event.target.checked)}
            />{' '}
            {t('lobby:create.resetScore')}
          </label>
        </p>
        <p>
          <label>
            <input
              type="checkbox"
              data-field="showCoordinates"
              checked={showCoordinates}
              onChange={(event) => setShowCoordinates(event.target.checked)}
            />{' '}
            {t('lobby:create.showCoordinates')}
          </label>
        </p>
        <p>{t('lobby:create.showCoordinatesHint')}</p>
        <p>{t('lobby:create.overrideHint')}</p>
      </fieldset>

      <fieldset>
        <legend>{t('lobby:create.hostHeading')}</legend>
        <p>
          <label>
            {t('lobby:create.hostName')}{' '}
            <input
              type="text"
              data-field="hostName"
              value={hostName}
              onChange={(event) => setHostName(event.target.value)}
            />
          </label>
        </p>
        <p>
          <label>
            {t('lobby:create.hostSector')}{' '}
            <select
              data-field="hostSector"
              value={effectiveHostSector ?? ''}
              onChange={(event) => setHostSector(event.target.value as SectorId)}
            >
              {slots.map((slot) => (
                <option key={slot.sector} value={slot.sector}>
                  {t(`sectors:${slot.sector}`)}
                </option>
              ))}
            </select>
          </label>
        </p>
      </fieldset>

      {complaint === undefined ? null : <p role="alert">{complaint}</p>}
      {failure === undefined ? null : (
        <div role="alert">
          <p>{t('lobby:create.failed')}</p>
          <p>{t(`errors:${failure.code}`)}</p>
          {failure.message === '' ? null : <p>{failure.message}</p>}
        </div>
      )}

      <p>
        <button type="button" onClick={submit}>
          {t('lobby:create.submit')}
        </button>
      </p>
    </section>
  );
}
